'use client';

import React, {
  FC, ReactNode, useEffect, useRef,
} from 'react';
import { createNoise3D } from 'simplex-noise';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface VortexProps {
  children?: ReactNode;
  className?: string;
  containerClassName?: string;
  particleCount?: number;
  rangeY?: number;
  baseHue?: number;
  baseSpeed?: number;
  rangeSpeed?: number;
  baseRadius?: number;
  rangeRadius?: number;
  backgroundColor?: string;
  canvasClassName?: string;
}

const TAU = 2 * Math.PI;
const PARTICLE_PROP_COUNT = 9;
const BASE_TTL = 50;
const RANGE_TTL = 150;
const RANGE_HUE = 100;
const NOISE_STEPS = 3;
const X_OFF = 0.00125;
const Y_OFF = 0.00125;
const Z_OFF = 0.0005;
// Retina backing stores quadruple the fill cost of the two blur passes for no visible gain here.
const MAX_DPR = 1.5;

const rand = (n: number): number => n * Math.random();
const randRange = (n: number): number => n - rand(2 * n);
const lerp = (n1: number, n2: number, speed: number): number => (1 - speed) * n1 + speed * n2;

const fadeInOut = (t: number, m: number): number => {
  const hm = 0.5 * m;
  return Math.abs(((t + hm) % m) - hm) / hm;
};

export const Vortex: FC<VortexProps> = ({
  children,
  className,
  containerClassName,
  canvasClassName,
  particleCount = 700,
  rangeY = 100,
  baseHue = 220,
  baseSpeed = 0.0,
  rangeSpeed = 1.5,
  baseRadius = 1,
  rangeRadius = 2,
  backgroundColor = '#000000',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container) {
      return undefined;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return undefined;
    }

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const propsLength = particleCount * PARTICLE_PROP_COUNT;
    const noise3D = createNoise3D();
    const center: [number, number] = [0, 0];

    let particleProps = new Float32Array(propsLength);
    let tick = 0;
    let scale = 1;
    let rafId = 0;
    let running = false;
    let inView = true;
    let pageVisible = true;

    const initParticle = (i: number) => {
      particleProps.set([
        rand(canvas.width),
        center[1] + randRange(rangeY * scale),
        0,
        0,
        0,
        BASE_TTL + rand(RANGE_TTL),
        (baseSpeed + rand(rangeSpeed)) * scale,
        (baseRadius + rand(rangeRadius)) * scale,
        baseHue + rand(RANGE_HUE),
      ], i);
    };

    const initParticles = () => {
      tick = 0;
      particleProps = new Float32Array(propsLength);

      for (let i = 0; i < propsLength; i += PARTICLE_PROP_COUNT) {
        initParticle(i);
      }
    };

    const updateParticle = (i: number) => {
      const x = particleProps[i];
      const y = particleProps[i + 1];
      const n = noise3D(x * X_OFF, y * Y_OFF, tick * Z_OFF) * NOISE_STEPS * TAU;
      const vx = lerp(particleProps[i + 2], Math.cos(n), 0.5);
      const vy = lerp(particleProps[i + 3], Math.sin(n), 0.5);
      const life = particleProps[i + 4] + 1;
      const ttl = particleProps[i + 5];
      const speed = particleProps[i + 6];
      const radius = particleProps[i + 7];
      const hue = particleProps[i + 8];
      const x2 = x + vx * speed;
      const y2 = y + vy * speed;

      ctx.lineWidth = radius;
      ctx.strokeStyle = `hsla(${hue},100%,60%,${fadeInOut(life, ttl)})`;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      particleProps[i] = x2;
      particleProps[i + 1] = y2;
      particleProps[i + 2] = vx;
      particleProps[i + 3] = vy;
      particleProps[i + 4] = life;

      if (x > canvas.width || x < 0 || y > canvas.height || y < 0 || life > ttl) {
        initParticle(i);
      }
    };

    const renderFrame = () => {
      tick += 1;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.lineCap = 'round';

      for (let i = 0; i < propsLength; i += PARTICLE_PROP_COUNT) {
        updateParticle(i);
      }

      ctx.restore();

      ctx.save();
      ctx.filter = 'blur(8px) brightness(200%)';
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();

      ctx.save();
      ctx.filter = 'blur(4px) brightness(200%)';
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();

      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(canvas, 0, 0);
      ctx.restore();
    };

    const loop = () => {
      renderFrame();
      rafId = window.requestAnimationFrame(loop);
    };

    const start = () => {
      if (running || prefersReduced || !inView || !pageVisible) {
        return;
      }

      running = true;
      rafId = window.requestAnimationFrame(loop);
    };

    const stop = () => {
      if (!running) {
        return;
      }

      window.cancelAnimationFrame(rafId);
      running = false;
    };

    // Size the backing store to the CONTAINER, not the window. The original sized it to
    // window.innerWidth/innerHeight inside an h-64 box, painting ~8x the pixels ever shown.
    const resize = () => {
      const rect = container.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      scale = Math.min(window.devicePixelRatio || 1, MAX_DPR);

      const width = Math.round(rect.width * scale);
      const height = Math.round(rect.height * scale);

      if (canvas.width === width && canvas.height === height) {
        return;
      }

      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${Math.round(rect.width)}px`;
      canvas.style.height = `${Math.round(rect.height)}px`;

      center[0] = 0.5 * width;
      center[1] = 0.5 * height;

      initParticles();
    };

    resize();
    start();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    // Nothing should burn frames while the section is scrolled away or the tab is hidden.
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;

        if (inView) {
          start();
        } else {
          stop();
        }
      },
      { rootMargin: '150px' },
    );
    intersectionObserver.observe(container);

    const handleVisibility = () => {
      pageVisible = !document.hidden;

      if (pageVisible) {
        start();
      } else {
        stop();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [
    particleCount, rangeY, baseHue, baseSpeed, rangeSpeed, baseRadius, rangeRadius, backgroundColor,
  ]);

  return (
    <div className={cn('relative', containerClassName)}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        ref={containerRef}
        className="absolute h-full w-full inset-0 z-0 bg-transparent flex items-center justify-center"
      >
        <canvas ref={canvasRef} className={canvasClassName} />
      </motion.div>

      <div className={cn('relative z-10', className)}>
        {children}
      </div>
    </div>
  );
};
